/**
 * Cost: what the selection cost in the time window, what one unit of work
 * cost, and where the money went.
 *
 * Total spend is the AWS bill with its model lines taken out and Numa's own
 * price for the same model usage put in. The tab shows both, so a person can
 * see what the adjustment moved and why.
 *
 * Every number comes from selectors.ts with the period it covers. A number
 * that follows the time window has no tag. Any other number says its period.
 */
import { useEffect, useMemo, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { Nav, Table } from 'react-bootstrap';
import { Line } from 'react-chartjs-2';
import type { ChartData, ChartOptions } from 'chart.js';
import '../chartSetup';
import { ND_COLORS, ND_FILL } from '@/styles/theme';
import type { DashboardView } from '@/types/fleetAnalytics';
import { useCurrency } from '../currencyContext';
import type { FleetData } from '../fleetData';
import {
  isReady,
  needsOf,
  selectActiveUsers,
  selectBedrockUsageTypes,
  selectCategories,
  selectChat,
  selectModelShare,
  selectOutliers,
  selectScheduled,
  selectServiceMix,
  selectSpend,
  selectStackRows,
  selectTokens,
  selectUnitCosts,
  selectUnitCostSeries,
} from '../selectors';
import type { Figure, Outlier, Spend, StackRow, Tiered, UnitCostMetric } from '../selectors';
import { fmtN, fmtUSD, fmtUSDc, pct, windowDays } from '../shared';
import type { WindowState } from '../shared';
import { Empty, Fact, FactGroup, Facts, Kpi, KpiGrid, NeedsTier, Panel } from '../ui/kit';
import {
  Bar,
  dayLabel,
  gridCols,
  inactiveSpendWords,
  moneyScale,
  notBilledWords,
  plural,
  Quiet,
  rowPool,
  SearchBox,
  settled,
  subLines,
  tile,
  waitingTile,
} from './common';
import type { DashboardTabProps } from './registry';
import './Cost.css';

const TOTAL_SPEND_HINT =
  'The AWS bill, with model usage that ran on a shared account moved to the stack that used it. Model usage is priced by Numa from token counts.';
const LENT_HINT =
  'Quota sharing: model usage by other stacks that AWS billed to this account. Total spend leaves it out.';
const BORROWED_HINT = 'Quota sharing: model usage here that AWS billed to another account. Total spend adds it back.';

/** A table with more rows than this gets a search box. */
const SEARCH_FROM_ROWS = 30;

/** Token counts are in the millions and billions: "11.6B", "869M", "8.2M". */
function fmtTokens(n: number): string {
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return fmtN(n);
}

export function Cost({ data, window: win, snapshots, fleet, selection, onSelectClient }: DashboardTabProps) {
  // The money formats read the chosen currency. This redraws the tab when it changes.
  const currency = useCurrency();
  const currencyKey = `${currency.code}:${currency.rate}`;

  const spend = useMemo(() => selectSpend(data, win), [data, win]);
  const chat = useMemo(() => selectChat(data, win), [data, win]);
  const scheduled = useMemo(() => selectScheduled(data, win), [data, win]);
  const activePeople = useMemo(() => selectActiveUsers(data, win), [data, win]);
  const unit = useMemo(() => selectUnitCosts(data, win), [data, win]);

  const isView = selection.kind === 'view';
  const stackRows = useMemo(
    () =>
      selectStackRows(rowPool(selection, snapshots), win, fleet.freshness, {
        contractOf: selection.contractOf,
        inactive: selection.inactive,
      }),
    [selection, snapshots, win, fleet.freshness]
  );
  const sharedWith = isView ? [] : (stackRows.rows.find((r) => r.client === selection.id)?.sharesAccountWith ?? []);

  // Total spend and cost per active person wait on heavier data. The rest of the tab draws without it.
  const wanted = needsOf(spend, unit.perActivePerson).join(',');
  const { ensureTier } = fleet;
  useEffect(() => {
    for (const tier of needsOf(spend, unit.perActivePerson)) ensureTier(tier);
    // `wanted` names the tiers: the answers themselves are new objects on every draw.
  }, [wanted, ensureTier]);

  const ready = isReady(spend) ? spend : null;
  /** One figure of the spend answer, or the tiers it waits for. */
  const part = (key: 'inferred' | 'awsBill' | 'lent' | 'borrowed'): Tiered<Figure> =>
    isReady(spend) ? settled(spend[key], spend) : spend;
  const modelShare = useMemo(() => (isReady(spend) ? settled(selectModelShare(spend), spend) : spend), [spend]);

  // The two quota sharing figures show only when the selector calls the selection a lender or a borrower.
  // Days AWS has not billed yet are in neither figure.
  const lends = ready && (ready.role === 'lender' || ready.role === 'mixed') && ready.lent.value > 0;
  const borrows = ready && (ready.role === 'borrower' || ready.role === 'mixed') && ready.borrowed.value > 0;

  let billSub: string | undefined;
  if (ready?.notNuma && ready.notNuma.value > 0) {
    billSub = `Numa's part of the bill. ${fmtUSD(ready.notNuma.value)} on ${isView ? "customers' own systems" : "the customer's own systems"} is left out${ready.notNuma.scope === 'all-time' ? ', all time' : ''}.`;
  } else if (sharedWith.length > 0) {
    billSub = `The whole bill of an AWS account shared with ${plural(sharedWith.length, 'other stack')}`;
  } else if (isView) {
    billSub = 'Each AWS account is counted once';
  }

  return (
    <div className="dash-cost">
      <KpiGrid cols={gridCols(3 + (lends ? 1 : 0) + (borrows ? 1 : 0))}>
        <Kpi
          lead
          label="Total spend"
          {...waitingTile(
            fleet,
            part('inferred'),
            fmtUSD,
            ready
              ? `${TOTAL_SPEND_HINT} Here it holds ${fmtUSD(ready.chat.value)} of model usage in place of ${fmtUSD(ready.modelLines.value)} on the bill.`
              : TOTAL_SPEND_HINT
          )}
          sub={ready ? subLines(notBilledWords(ready), inactiveSpendWords(ready.inactive, 'spend')) : undefined}
        />
        <Kpi
          label="AWS bill before adjustment"
          {...waitingTile(fleet, part('awsBill'), fmtUSD, 'What AWS billed, model usage included as AWS priced it.')}
          sub={subLines(billSub, ready ? inactiveSpendWords(ready.inactive, 'awsBill') : undefined)}
        />
        {lends && <Kpi label="Model usage paid for other stacks" {...tile(part('lent'), fmtUSD, LENT_HINT)} />}
        {borrows && (
          <Kpi label="Model usage paid by another account" {...tile(part('borrowed'), fmtUSD, BORROWED_HINT)} />
        )}
        <Kpi
          label="Model spend share of total"
          {...waitingTile(
            fleet,
            modelShare,
            (share: number) => pct(share, 1),
            'Model spend from chat, agents and scheduled runs as a share of total spend.'
          )}
          sub={ready ? `${fmtUSD(ready.chat.value)} of ${fmtUSD(ready.inferred.value)}` : undefined}
        />
      </KpiGrid>

      <div className="dash-cols dash-cols--2-1">
        <Panel
          title="Total spend against the AWS bill"
          subtitle="Total spend is the AWS bill with its model lines swapped for Numa's price for the same usage."
        >
          <SpendAgainstBill spend={spend} fleet={fleet} currencyKey={currencyKey} />
        </Panel>

        <Panel title="Unit costs" subtitle="Model spend divided by each count.">
          <Facts cols={1}>
            <FactGroup>
              <Fact
                label="Cost per conversation"
                {...tile(unit.perConversation, fmtUSD, `${fmtN(chat.conversations.value)} conversations.`)}
              />
              <Fact
                label="Cost per message"
                {...tile(unit.perMessage, fmtUSDc, `${fmtN(chat.messages.value)} messages.`)}
              />
              <Fact
                label="Cost per active person"
                {...waitingTile(
                  fleet,
                  unit.perActivePerson,
                  fmtUSD,
                  isReady(activePeople) ? `${plural(activePeople.value, 'person', 'people')} active.` : undefined
                )}
              />
              <Fact
                label="Cost per scheduled run"
                {...tile(
                  unit.perScheduledRun,
                  fmtUSD,
                  `Model spend of scheduled and triggered runs divided by the runs that finished in the window: ${plural(scheduled.runs.value, 'run')}, ${fmtUSD(scheduled.cost.value)}.`
                )}
              />
            </FactGroup>
          </Facts>
        </Panel>
      </div>

      <ByKindOfWork data={data} win={win} />

      <div className="dash-cols dash-cost__pair">
        <ByService data={data} win={win} fleet={fleet} />
        <BedrockUsage data={data} win={win} fleet={fleet} />
      </div>

      <TokenMix data={data} win={win} />

      <UnitCostOverTime data={data} win={win} currencyKey={currencyKey} />

      {isView && <HighUnitCost rows={stackRows.rows} onSelectClient={onSelectClient} />}
    </div>
  );
}

// ─── total spend against the AWS bill ────────────────────────────────────

interface SpendAgainstBillProps {
  spend: Tiered<Spend>;
  fleet: FleetData;
  currencyKey: string;
}

function SpendAgainstBill({ spend, fleet, currencyKey }: SpendAgainstBillProps) {
  const chart = useMemo(() => {
    if (!isReady(spend)) return null;
    const { days, inferred, awsBill } = spend.daily;
    const chartData: ChartData<'line'> = {
      labels: days.map(dayLabel),
      datasets: [
        {
          label: 'AWS bill before adjustment',
          data: awsBill,
          borderColor: ND_COLORS.accent2,
          backgroundColor: ND_FILL.accent2,
          borderWidth: 1.5,
          pointRadius: 0,
          pointHoverRadius: 3,
          tension: 0.25,
          fill: true,
        },
        {
          label: 'Total spend',
          data: inferred,
          borderColor: ND_COLORS.accent,
          backgroundColor: 'transparent',
          borderWidth: 2.5,
          pointRadius: 0,
          pointHoverRadius: 3,
          tension: 0.25,
        },
      ],
    };
    const options: ChartOptions<'line'> = {
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { position: 'top', align: 'end', labels: { boxWidth: 12, boxHeight: 2 } },
        tooltip: { callbacks: { label: (c) => `${c.dataset.label}: ${fmtUSD(c.parsed.y)}` } },
      },
      scales: {
        x: { grid: { display: false }, ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 12 } },
        y: moneyScale(),
      },
    };
    return { chartData, options, empty: days.length === 0 };
    // The axis and the hover text are money: draw again when the currency changes.
  }, [spend, currencyKey]);

  if (!chart) {
    return (
      <NeedsTier fleet={fleet} tiers={['services']} what="Spend by day">
        <Empty>Spend by day is not ready yet.</Empty>
      </NeedsTier>
    );
  }
  if (chart.empty) return <Empty>The window has no days in it yet.</Empty>;
  return (
    <div className="dash-chart">
      <Line data={chart.chartData} options={chart.options} />
    </div>
  );
}

// ─── by kind of work ─────────────────────────────────────────────────────

interface ViewProps {
  data: DashboardView;
  win: WindowState;
}

function ByKindOfWork({ data, win }: ViewProps) {
  useCurrency();
  const categories = useMemo(() => selectCategories(data, win), [data, win]);
  const any = categories.rows.some((r) => r.cost > 0 || r.conversations > 0);

  return (
    <Panel
      title="By kind of work"
      scope={categories.scope}
      subtitle="Model spend by who started the conversation: a person in plain chat, a person with an agent, or a schedule."
      flush
    >
      {!any ? (
        <Empty>No chat or agent work in this window.</Empty>
      ) : (
        <div className="dash-scroll dash-scroll--free">
          <Table className="dash-table">
            <thead>
              <tr>
                <th>Kind of work</th>
                <th className="text-end">Cost</th>
                <th>Share of cost</th>
                <th className="text-end">Conversations</th>
                <th className="text-end">Messages</th>
                <th className="text-end">Cost per message</th>
                <th className="text-end">Cost per conversation</th>
              </tr>
            </thead>
            <tbody>
              {categories.rows.map((r) => (
                <tr key={r.key}>
                  <td className="dash-name">{r.label}</td>
                  <td className="text-end numeric">{fmtUSD(r.cost)}</td>
                  <td>
                    <Share share={r.shareOfCost} />
                  </td>
                  <td className="text-end numeric">{fmtN(r.conversations)}</td>
                  <td className="text-end numeric">{fmtN(r.messages)}</td>
                  <td className="text-end numeric">
                    {r.costPerMessage === null ? <Quiet /> : fmtUSDc(r.costPerMessage)}
                  </td>
                  <td className="text-end numeric">
                    {r.costPerConversation === null ? <Quiet /> : fmtUSD(r.costPerConversation)}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
      {any && categories.note && <p className="dash-note dash-note--strip">{categories.note}</p>}
    </Panel>
  );
}

/** A share as a short bar and a percentage. */
function Share({ share }: { share: number }) {
  return (
    <div className="dash-cost-share">
      <Bar fraction={share} className="dash-cost-share__bar" />
      <span className="dash-cost-share__pct numeric">{pct(share, 1)}</span>
    </div>
  );
}

// ─── by AWS service, and inside the Bedrock line ─────────────────────────

interface TierProps extends ViewProps {
  fleet: FleetData;
}

function ByService({ data, win, fleet }: TierProps) {
  useCurrency();
  const mix = useMemo(() => selectServiceMix(data, win), [data, win]);
  const [query, setQuery] = useState('');
  const ready = isReady(mix) ? mix : null;
  const needle = query.trim().toLowerCase();
  const rows = ready ? ready.rows.filter((r) => !needle || r.service.toLowerCase().includes(needle)) : [];

  return (
    <Panel
      title="By AWS service"
      scope={ready?.scope}
      subtitle={`Total spend by line of the AWS bill, with model usage as one line at Numa's price.${ready?.note ? ` ${ready.note}` : ''}`}
      actions={
        ready && ready.rows.length > SEARCH_FROM_ROWS ? (
          <SearchBox value={query} onChange={setQuery} placeholder="Find a service" />
        ) : undefined
      }
      flush
    >
      <NeedsTier fleet={fleet} tiers={['services']} what="Spend by service">
        {!ready || ready.rows.length === 0 ? (
          <Empty>No spend in this window.</Empty>
        ) : rows.length === 0 ? (
          <Empty>No service matches &quot;{query.trim()}&quot;.</Empty>
        ) : (
          <div className="dash-scroll dash-scroll--short">
            <Table className="dash-table">
              <thead>
                <tr>
                  <th>Service</th>
                  <th className="text-end">Cost</th>
                  <th>Share</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.service}>
                    <td className="dash-name">{r.service}</td>
                    <td className="text-end numeric">{fmtUSD(r.usd)}</td>
                    <td>
                      <Share share={r.share} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </NeedsTier>
    </Panel>
  );
}

function BedrockUsage({ data, win, fleet }: TierProps) {
  useCurrency();
  const usage = useMemo(() => selectBedrockUsageTypes(data, win), [data, win]);
  const [query, setQuery] = useState('');
  const ready = isReady(usage) ? usage : null;
  const needle = query.trim().toLowerCase();
  const rows = ready ? ready.rows.filter((r) => !needle || r.usageType.toLowerCase().includes(needle)) : [];

  return (
    <Panel
      title="Inside the Amazon Bedrock line"
      scope={ready?.scope}
      subtitle="Usage types inside the Amazon Bedrock line. Claude and OpenAI models are billed on other lines."
      actions={
        ready && ready.rows.length > SEARCH_FROM_ROWS ? (
          <SearchBox value={query} onChange={setQuery} placeholder="Find a usage type" />
        ) : undefined
      }
      flush
    >
      <NeedsTier fleet={fleet} tiers={['services']} what="Bedrock usage">
        {!ready || ready.rows.length === 0 ? (
          <Empty>
            {ready?.scope === 'window'
              ? 'No Amazon Bedrock spend in this window.'
              : 'No Amazon Bedrock spend on record.'}
          </Empty>
        ) : rows.length === 0 ? (
          <Empty>No usage type matches &quot;{query.trim()}&quot;.</Empty>
        ) : (
          <div className="dash-scroll dash-scroll--short">
            <Table className="dash-table">
              <thead>
                <tr>
                  <th>Usage type</th>
                  <th className="text-end">Cost</th>
                  <th>Share</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.usageType}>
                    <td className="mono dash-cost-table__code" title={r.usageType}>
                      {r.usageType}
                    </td>
                    <td className="text-end numeric">{fmtUSD(r.usd)}</td>
                    <td>
                      <Share share={r.share} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </NeedsTier>
    </Panel>
  );
}

// ─── tokens ──────────────────────────────────────────────────────────────

function TokenMix({ data, win }: ViewProps) {
  const tokens = useMemo(() => selectTokens(data, win), [data, win]);
  const kinds = [
    { key: 'read', label: 'Input read from the cache', value: tokens.cacheRead },
    { key: 'write', label: 'Input written to the cache', value: tokens.cacheWrite },
    { key: 'input', label: 'Input, not cached', value: tokens.input },
    { key: 'output', label: 'Output', value: tokens.output },
  ];
  // The length of each bar. The counts and the cache hit ratio come from the selector as they are.
  const all = kinds.reduce((a, k) => a + k.value, 0);

  return (
    <Panel
      title="Token mix"
      scope={tokens.scope}
      subtitle={
        tokens.cacheHitRatio === null
          ? 'No model usage to count.'
          : `Cache hit ratio ${pct(tokens.cacheHitRatio, 1)}: the share of input tokens read from the cache, which cost less than fresh input.${tokens.note ? ` ${tokens.note}` : ''}`
      }
    >
      {all <= 0 ? (
        <Empty>{tokens.scope === 'window' ? 'No tokens in this window.' : 'No tokens on record.'}</Empty>
      ) : (
        <ul className="dash-cost-tokens">
          {kinds.map((k) => (
            <li key={k.key} className="dash-cost-tokens__row">
              <span className="dash-cost-tokens__name">{k.label}</span>
              <Bar fraction={k.value / all} className="dash-cost-tokens__bar" />
              <span className="dash-cost-tokens__count numeric" title={`${fmtN(k.value)} tokens`}>
                {fmtTokens(k.value)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

// ─── a unit cost over time ───────────────────────────────────────────────

const UNIT_METRICS: Array<{
  key: UnitCostMetric;
  /** The words on the picker. */
  label: string;
  /** The line's name in the chart. */
  series: string;
  about: string;
  format: (n: number) => string;
}> = [
  {
    key: 'perMessage',
    label: 'Per message',
    series: 'Cost per message',
    about: 'Model spend each day divided by the messages people sent that day.',
    format: fmtUSDc,
  },
  {
    key: 'perConversation',
    label: 'Per conversation',
    series: 'Cost per conversation',
    about: 'Model spend each day divided by the conversations that day.',
    format: fmtUSD,
  },
  {
    key: 'perActivePersonDay',
    label: 'Per person per active day',
    series: 'Cost per person per active day',
    about:
      'Model spend each day divided by the people active that day. It reads lower than cost per active person, which counts each person once for the whole window.',
    format: fmtUSD,
  },
];

function UnitCostOverTime({ data, win, currencyKey }: ViewProps & { currencyKey: string }) {
  const [metricKey, setMetricKey] = useState<UnitCostMetric>('perMessage');
  const metric = UNIT_METRICS.find((m) => m.key === metricKey) ?? UNIT_METRICS[0];
  // A week smooths out the weekend. Over a long window a month reads better.
  const span = windowDays(win) > 92 ? 30 : 7;
  const series = useMemo(() => selectUnitCostSeries(data, win, metric.key, span), [data, win, metric.key, span]);

  const chart = useMemo(() => {
    const chartData: ChartData<'line'> = {
      labels: series.days.map(dayLabel),
      datasets: [
        {
          label: `${metric.series}, by day`,
          data: series.daily,
          borderColor: ND_FILL.accentStrong,
          backgroundColor: ND_FILL.accentStrong,
          borderWidth: 1.5,
          pointRadius: 1.5,
          pointHoverRadius: 3,
          tension: 0.25,
        },
        {
          label: `Over the ${series.trailingDays} days up to each day`,
          data: series.trailing,
          borderColor: ND_COLORS.accent,
          backgroundColor: ND_COLORS.accent,
          borderWidth: 2.5,
          pointRadius: 0,
          pointHoverRadius: 3,
          tension: 0.3,
          spanGaps: true,
        },
      ],
    };
    const options: ChartOptions<'line'> = {
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { position: 'top', align: 'end', labels: { boxWidth: 12, boxHeight: 2 } },
        tooltip: {
          callbacks: {
            label: (c) => `${c.dataset.label}: ${c.parsed.y === null ? 'no activity' : metric.format(c.parsed.y)}`,
          },
        },
      },
      scales: {
        x: { grid: { display: false }, ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 12 } },
        y: moneyScale(),
      },
    };
    return { chartData, options };
    // The axis and the hover text are money: draw again when the currency changes.
  }, [series, metric, currencyKey]);

  // A day with none of the thing is a gap in the line. A window of nothing but gaps has no chart to draw.
  const hasPoints = series.available && series.daily.some((v) => v !== null);

  return (
    <Panel
      title="Unit cost over time"
      subtitle={`${metric.about}${series.note ? ` ${series.note}` : ''}`}
      actions={
        <Nav
          variant="pills"
          className="nav-pills-custom dash-cost__picker"
          activeKey={metric.key}
          onSelect={(key) => key && setMetricKey(key as UnitCostMetric)}
        >
          {UNIT_METRICS.map((m) => (
            <Nav.Item key={m.key}>
              <Nav.Link eventKey={m.key}>{m.label}</Nav.Link>
            </Nav.Item>
          ))}
        </Nav>
      }
    >
      {hasPoints ? (
        <div className="dash-chart">
          <Line data={chart.chartData} options={chart.options} />
        </div>
      ) : (
        <Empty>
          {series.available
            ? 'No chat or agent work in this window.'
            : 'This data has no daily record to work this out from.'}
        </Empty>
      )}
    </Panel>
  );
}

// ─── a view: stacks with a high unit cost ────────────────────────────────

/** The two unit costs this list compares. The selector also ranks stacks by total spend, which the Overview lists. */
const UNIT_OUTLIERS: Partial<Record<Outlier['metric'], { label: string; format: (n: number) => string }>> = {
  costPerMessage: { label: 'Cost per message', format: fmtUSDc },
  costPerConversation: { label: 'Cost per conversation', format: fmtUSD },
};

interface HighUnitCostProps {
  rows: StackRow[];
  onSelectClient: (id: string | null) => void;
}

function HighUnitCost({ rows, onSelectClient }: HighUnitCostProps) {
  useCurrency();
  const outliers = useMemo(() => selectOutliers(rows).filter((o) => UNIT_OUTLIERS[o.metric]), [rows]);
  const byClient = useMemo(() => new Map(rows.map((r) => [r.client, r])), [rows]);

  const openOnKey = (e: KeyboardEvent<HTMLTableRowElement>, client: string) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    onSelectClient(client);
  };

  return (
    <Panel
      title="Stacks with a high unit cost"
      subtitle="Stacks whose model cost per message or per conversation is more than two standard deviations above the mean of the stacks in this window."
      flush
    >
      {outliers.length === 0 ? (
        <Empty>No stack is far above the rest in this window.</Empty>
      ) : (
        <div className="dash-scroll dash-scroll--short">
          <Table hover className="dash-table">
            <thead>
              <tr>
                <th>Stack</th>
                <th>Unit cost</th>
                <th className="text-end">This stack</th>
                <th className="text-end">Mean of the stacks</th>
                <th className="text-end">Standard deviations above</th>
                <th className="text-end">Model spend</th>
              </tr>
            </thead>
            <tbody>
              {outliers.map((o) => {
                const unit = UNIT_OUTLIERS[o.metric];
                const row = byClient.get(o.client);
                if (!unit) return null;
                return (
                  <tr
                    key={`${o.client}:${o.metric}`}
                    className="dash-row"
                    role="button"
                    tabIndex={0}
                    onClick={() => onSelectClient(o.client)}
                    onKeyDown={(e) => openOnKey(e, o.client)}
                  >
                    <td className="dash-name">{row?.label ?? o.client}</td>
                    <td>{unit.label}</td>
                    <td className="text-end numeric">{unit.format(o.value)}</td>
                    <td className="text-end numeric">{unit.format(o.mean)}</td>
                    <td className="text-end numeric">{o.zScore.toFixed(1)}</td>
                    <td className="text-end numeric">{row ? fmtUSD(row.chatCost) : <Quiet />}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}
      {outliers.length > 0 && (
        <p className="dash-note dash-note--strip">
          A stack with little activity in the window is not compared. Select a row to open the stack.
        </p>
      )}
    </Panel>
  );
}
